import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';

import { formatApiDateTime } from '../admin/dateTimeUtils';
import { examNames, type ExamName } from '../examTasks/examTaskTypes';
import {
    deleteAdminTemplate,
    getAdminTemplateList,
    type AdminTemplateFile,
    uploadAdminTemplate,
} from '../admin/adminTemplatesApi';

function formatFileSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export function TemplateManagementPage() {
    const notify = useNotify();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [templates, setTemplates] = useState<AdminTemplateFile[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [examFilter, setExamFilter] = useState<ExamName | ''>('');
    const [uploadExamName, setUploadExamName] = useState<ExamName>('Academic');
    const [title, setTitle] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const groupedTemplates = useMemo(() => {
        const groups = new Map<string, { examName: ExamName; title: string; files: AdminTemplateFile[] }>();
        for (const item of templates) {
            const key = `${item.examName}:${item.title.trim().toLowerCase()}`;
            const existing = groups.get(key);
            if (existing) {
                existing.files.push(item);
            } else {
                groups.set(key, {
                    examName: item.examName,
                    title: item.title,
                    files: [item],
                });
            }
        }
        return Array.from(groups.values());
    }, [templates]);

    const groupedTemplatesByExam = useMemo(() => {
        return examNames
            .map((examName) => ({
                examName,
                groups: groupedTemplates.filter((group) => group.examName === examName),
            }))
            .filter((section) => section.groups.length > 0);
    }, [groupedTemplates]);

    const titleOptions = useMemo(() => {
        const existingTitles = new Map<string, string>();
        for (const item of templates) {
            if (item.examName !== uploadExamName) {
                continue;
            }

            const normalizedTitle = item.title.trim();
            if (normalizedTitle) {
                existingTitles.set(normalizedTitle.toLowerCase(), normalizedTitle);
            }
        }

        return Array.from(existingTitles.values()).sort((left, right) => left.localeCompare(right));
    }, [templates, uploadExamName]);

    const loadTemplates = useCallback(async (nextSearch = search, nextExamName = examFilter) => {
        setIsLoading(true);
        try {
            const result = await getAdminTemplateList({
                page: 1,
                pageSize: 200,
                search: nextSearch,
                examName: nextExamName,
            });
            setTemplates(result.items);
            setTotalCount(result.totalCount);
        } catch (error) {
            setTemplates([]);
            setTotalCount(0);
            const message = error instanceof Error ? error.message : 'Unable to load templates';
            notify(message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [examFilter, notify, search]);

    useEffect(() => {
        void loadTemplates();
    }, [loadTemplates]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFile(event.target.files?.[0] ?? null);
    };

    const handleUpload = async () => {
        const normalizedTitle = title.trim();
        if (!normalizedTitle) {
            notify('Title is required', { type: 'warning' });
            return;
        }
        if (!file) {
            notify('PDF file is required', { type: 'warning' });
            return;
        }
        if (file.type && file.type !== 'application/pdf') {
            notify('Only PDF files are supported', { type: 'warning' });
            return;
        }

        setIsUploading(true);
        try {
            await uploadAdminTemplate({
                examName: uploadExamName,
                title: normalizedTitle,
                file,
            });
            notify('Template uploaded', { type: 'info' });
            setTitle('');
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            await loadTemplates(search, examFilter);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to upload template';
            notify(message, { type: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (item: AdminTemplateFile) => {
        if (!window.confirm(`Delete "${item.fileName}"?`)) {
            return;
        }

        setDeletingId(item.templateFileId);
        try {
            await deleteAdminTemplate(item.templateFileId);
            notify('Template deleted', { type: 'info' });
            await loadTemplates(search, examFilter);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to delete template';
            notify(message, { type: 'error' });
        } finally {
            setDeletingId(null);
        }
    };

    const isBusy = isLoading || isUploading || deletingId !== null;

    return (
        <Box>
            <Title title="Templates" />
            {isBusy ? <LinearProgress /> : null}
            <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h4">Templates</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {totalCount} file{totalCount === 1 ? '' : 's'}
                            {examFilter ? ` for ${examFilter}` : ''}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshRoundedIcon />}
                        onClick={() => void loadTemplates(search, examFilter)}
                    >
                        Refresh
                    </Button>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel id="template-upload-exam-label">Exam</InputLabel>
                            <Select
                                labelId="template-upload-exam-label"
                                label="Exam"
                                value={uploadExamName}
                                onChange={(event) => setUploadExamName(event.target.value as ExamName)}
                            >
                                {examNames.map((name) => (
                                    <MenuItem key={name} value={name}>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Autocomplete
                            freeSolo
                            options={titleOptions}
                            value={title}
                            inputValue={title}
                            onChange={(_, value) => setTitle(typeof value === 'string' ? value : '')}
                            onInputChange={(_, value) => setTitle(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Title"
                                />
                            )}
                            sx={{ minWidth: { lg: 280 }, flex: 1 }}
                        />
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<PictureAsPdfOutlinedIcon />}
                            sx={{ justifyContent: 'flex-start', minWidth: { lg: 240 } }}
                        >
                            {file ? file.name : 'Choose PDF'}
                            <input ref={fileInputRef} hidden type="file" accept="application/pdf,.pdf" onChange={handleFileChange} />
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<UploadFileRoundedIcon />}
                            onClick={handleUpload}
                            disabled={isUploading}
                        >
                            Upload
                        </Button>
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                        <TextField
                            label="Search"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            sx={{ flex: 1 }}
                        />
                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel id="template-filter-exam-label">Exam</InputLabel>
                            <Select
                                labelId="template-filter-exam-label"
                                label="Exam"
                                value={examFilter}
                                onChange={(event) => setExamFilter(event.target.value as ExamName | '')}
                            >
                                <MenuItem value="">All</MenuItem>
                                {examNames.map((name) => (
                                    <MenuItem key={name} value={name}>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="outlined" onClick={() => setSearch(searchInput.trim())}>
                            Search
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => {
                                setSearchInput('');
                                setSearch('');
                                setExamFilter('');
                            }}
                        >
                            Clear
                        </Button>
                    </Stack>
                </Paper>

                {groupedTemplatesByExam.length === 0 ? (
                    <Alert severity="info">No templates found.</Alert>
                ) : (
                    <Stack spacing={2}>
                        {groupedTemplatesByExam.map((section) => (
                            <Box key={section.examName}>
                                <Typography variant="h5" sx={{ mb: 1.5 }}>
                                    {section.examName}
                                </Typography>
                                <Stack spacing={2}>
                                    {section.groups.map((group) => (
                                        <Paper
                                            key={`${group.examName}-${group.title}`}
                                            variant="outlined"
                                            sx={{ overflow: 'hidden' }}
                                        >
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={1}
                                                alignItems={{ sm: 'center' }}
                                                sx={{ px: 2.5, py: 2 }}
                                            >
                                                <FolderOutlinedIcon color="action" />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
                                                        {group.title}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                                                        <Chip
                                                            size="small"
                                                            label={`${group.files.length} file${group.files.length === 1 ? '' : 's'}`}
                                                        />
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                            <Divider />
                                            <Stack divider={<Divider />}>
                                                {group.files.map((item) => (
                                                    <Stack
                                                        key={item.templateFileId}
                                                        direction={{ xs: 'column', md: 'row' }}
                                                        spacing={1.5}
                                                        alignItems={{ md: 'center' }}
                                                        sx={{ px: 2.5, py: 1.75 }}
                                                    >
                                                        <PictureAsPdfOutlinedIcon color="error" />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                                                {item.fileName}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatFileSize(item.fileSizeBytes)} - Updated {formatApiDateTime(item.updatedAt)}
                                                            </Typography>
                                                        </Box>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<DeleteOutlineRoundedIcon />}
                                                            onClick={() => void handleDelete(item)}
                                                            disabled={deletingId === item.templateFileId}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}
